import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    handleStartNewProject,
    getStartNewJobFormDataInitialState,
    checkIfAllRequiredInputsAreFilled,
} from '../../../helpers/startNewJobFormUtils';

import { defaultErrorMessage } from '../../../constants/errorMessages';

import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import NewJobForm from '../../../components/NewJobForm/NewJobForm';
import CircularIcon from '../../../design-system/CircularIcon/CircularIcon';
import ErrorMessage from '../../../design-system/ErrorMessage/ErrorMessage';
import CheckLineIcon from '../../../design-system/Icons/CheckLineIcon';
import FormTabActionButtons from '../FormTabActionButtons/FormTabActionButtons';
import { Button } from '../../../design-system';

const FormTabSection = ({ scenario }) => {
    const { data, error } = scenario || {};

    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState(() => {
        if (!data) return null;

        return getInitialFormData();
    });

    const newProjectIdRef = useRef(null);
    const [startNewProjectState, setStartNewProjectState] = useState('default'); // states are "default", "loading", "success"

    const controller = useRef(new AbortController());

    const [errorAlert, setErrorAlert] = useState(null);

    const allRequiredInputsAreFilled = useMemo(
        () => checkIfAllRequiredInputsAreFilled(formData || []),
        [formData]
    );

    useEffect(() => {
        if (!data || !!formData) return;

        return setFormData(getInitialFormData);
    }, [data]);

    function getInitialFormData() {
        return getStartNewJobFormDataInitialState({
            inputs: data.default_version.inputs || [],
            allowDocsMapping: true,
            replaceOnlyFilledTags: false,
        });
    }

    const onSubmit = async () => {
        controller.current?.abort();
        controller.current = new AbortController();

        const scenarioVersionId = data.default_version?.id;
        await handleStartNewProject({
            formData,
            setFormData,
            scenarioVersionId,
            setStartNewProjectState,
            setErrorAlert,
            setNewProjectId: (id) => (newProjectIdRef.current = id),
            options: { signal: controller.current.signal },
        });
    };

    const onClearForm = () => {
        controller.current?.abort();

        setFormData(getInitialFormData);
        setStartNewProjectState('default');
        newProjectIdRef.current = null;
    };

    const viewProjectDetails = () => {
        const newProjectId = newProjectIdRef.current;
        if (!newProjectId) return;

        navigate(`/project/${newProjectId}`, { state: { from: location } });
    };

    if (!scenario) return null;

    if (error) return <ErrorMessage message={defaultErrorMessage} size="medium" />;

    const submitButtonState =
        startNewProjectState === 'loading'
            ? 'loading'
            : allRequiredInputsAreFilled
            ? 'default'
            : 'disabled';

    return (
        <>
            {startNewProjectState !== 'success' && (
                <NewJobForm formData={formData} setFormData={setFormData} containerGap={10} />
            )}

            {startNewProjectState === 'success' && (
                <div className="px-8 w-full py-[40px]">
                    <div className="flex flex-col items-center gap-3 md:gap-4 pb-3 md:pb-4">
                        <CircularIcon icon={CheckLineIcon} color="lime" />

                        <p className="font-body text-body-regular-xs text-neutral-300 uppercase">
                            Project successfully started
                        </p>

                        <Button
                            type="link"
                            size="sm"
                            text="View Project Details"
                            onClick={viewProjectDetails}
                        />
                    </div>
                </div>
            )}

            <FormTabActionButtons
                onClearForm={onClearForm}
                onSubmit={onSubmit}
                startNewProjectState={startNewProjectState}
                submitButtonState={submitButtonState}
                hasInputs={!!formData?.length}
            />

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </>
    );
};

export default memo(FormTabSection);
